import gql from 'graphql-tag';
import { graphQuery, graphMutation, postFormRequest } from '@services/commService';
import { CLOUD_SERVER, CLOUD_ENDPOINTS } from '@services/servers';

export const getConversations = async () => {
  const query = `
  {
    conversations {
      id
      Users {
        id
        fullName
        avatar
        birthdate
        email
        self
        isMentor
      }
      Thread {
        id
        unread
        closed{
          at
          by
        }
        Posts (range: [0,1]) {
          id
          publisherUser{
            id
            fullName
            avatar
          }
          createdAt
          self
          payload 
        }
      }
    }
  }
  `;
  const res = await graphQuery(query, true);
  const { conversations } = res.data;
  return conversations;
};

export const getFullConversation = async (id) => {
  const query = `
  {
    conversations (id: "${id}") {
      id
      Users {
        id
        fullName
        avatar
        birthdate
        email
        self
        isMentor
      }
      Thread {
        id
        unread
        closed{
          at
          by
        }
        Posts {
          id
          publisherUser{
            id
            fullName
            avatar
          }
          createdAt
          self
          seenBy
          payload
        }
      }
    }  
  }
  `;

  const res = await graphQuery(query, true);
  const { conversations } = res.data;
  return conversations[0];
};

export const getUsers = async () => {
  const query = `
  {
    users {
      id
      fullName
      avatar
      birthdate
      email
      self
      isMentor
    }
  }
  `;

  const res = await graphQuery(query, true);
  const { users } = res.data;
  return users;
};

export const createConversation = async (participantIds) => {
  const variables = { participantIds };
  const mutation = `
  mutation(
    $participantIds: [String]!
    ){
      createConversation( 
      participantIds: $participantIds
    ) {
      id
      Thread {
        id
      }
    }
  }
`;
  const res = await graphMutation(mutation, variables, true);
  const conversation = res.data.createConversation;
  return {
    newThreadId: conversation.Thread.id,
    newConversationId: conversation.id,
  };
};

export const createPost = async (threadId, messagePayload) => {
  const variables = { threadId, payload: messagePayload };
  const mutation = `
  mutation(
    $threadId: String!
    $payload: JSON!
    ){
      createPost( 
        threadId: $threadId
        payload: $payload
    ) {
      id
    }
  }
`;
  const res = await graphMutation(mutation, variables, true);
  if (!res?.data?.createPost?.id) {
    throw new Error('Failed to create chat post');
  }
  const { id } = res.data.createPost;
  return id;
};

export const uploadChatMedia = async (file) => {
  const response = await postFormRequest(
    CLOUD_SERVER,
    CLOUD_ENDPOINTS.UPLOAD_CHAT_MEDIA,
    null,
    file,
  );

  return response.body;
};

export const createChatMedia = async ({
  type,
  title,
  description = '',
  value,
  metadata = {},
}) => {
  const variables = {
    type,
    title,
    description,
    value,
    metadata,
  };

  const mutation = `
  mutation(
    $type: String!
    $title: String!
    $description: String
    $value: String!
    $metadata: JSON
  ) {
    createChatMedia(
      type: $type
      title: $title
      description: $description
      value: $value
      metadata: $metadata
    ) {
      id
      type
      title
      metadata
    }
  }
`;

  const res = await graphMutation(mutation, variables, true);
  if (!res?.data?.createChatMedia?.id) {
    throw new Error('Failed to create chat media');
  }
  return res.data.createChatMedia;
};

export const createVoiceAttachment = async ({
  file,
  fileName,
  recordingDurationSeconds,
}) => {
  const uploadResponse = await uploadChatMedia(file);
  if (!uploadResponse?.uploadedFile) {
    throw new Error('Failed to upload chat media');
  }

  const media = await createChatMedia({
    type: 'audio',
    title: fileName,
    value: uploadResponse.uploadedFile,
    metadata: {
      mimeType: uploadResponse.mimeType || file.type,
      durationSeconds: recordingDurationSeconds,
      sizeBytes: uploadResponse.size || file.size,
      originalName: uploadResponse.originalName || fileName,
    },
  });

  if (!media?.id) {
    throw new Error('Failed to create chat media');
  }

  return {
    type: 'audio',
    mediaId: media.id,
    mimeType: media.metadata?.mimeType || uploadResponse.mimeType || file.type,
    sizeBytes: media.metadata?.sizeBytes || uploadResponse.size || file.size,
  };
};

export const createVoiceMessagePayload = ({
  voiceAttachment,
  recordingDurationSeconds,
}) => ({
  kind: 'voice',
  text: '',
  attachments: [
    {
      ...voiceAttachment,
      durationSeconds: recordingDurationSeconds,
    },
  ],
});

export const markPostsAsSeen = async (ids) => {
  const variables = { id: ids, type: 'POST' };
  const mutation = `
  mutation(
    $id: [String]!
    $type: ActivityEntityEnumType!
    ){
      visit( 
        id: $id
        type: $type
    ) {
      id
    }
  }
`;
  const res = await graphMutation(mutation, variables, true);
  if (!res?.data?.visit) {
    return [];
  }
  const visits = res.data.visit;
  return visits;
};

export const editPost = async (postId, payload) => {
  const mutation = gql`mutation{
    post:editPost(
      id:"${postId}"
      text:"""${payload.text}"""
      ) {
      id
      createdAt
      payload
      publisherUser{
        avatar
        fullName
      }
    }
  }`;
  const res = await graphMutation(mutation);

  return res;
};

export const deletePost = async (postId) => {
  const variables = { id: postId };
  const mutation = `
  mutation(
    $id: String!
    ){
      deletePost( 
        id: $id
    ) {
      success
      id
    }
  }
`;
  const res = await graphMutation(mutation, variables, true);
  if (!res?.data?.deletePost) {
    return { success: false, id: postId };
  }
  const { success, id } = res.data.deletePost;
  return { success, id };
};
